/**
 * The schedule picker the Automations page and Workflows share: frequency,
 * time, days, interval and optional start and end dates, over the shared
 * `@repo/shared/automationSchedule` rules (the server normalizes what it
 * produces), plus the browser's zone as a schedule's default.
 *
 * @module components/automations/SchedulePicker
 */
import { WEEKDAY_LABELS, zonedDateKey, type AutomationFrequency } from "@repo/shared/automationSchedule";
import { ChevronDownIcon, XIcon } from "lucide-react";
import { useState, type ReactNode } from "react";

import { cn } from "../../lib/utils";
import type { AutomationSchedule } from "../../state/automations";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Popover, PopoverPopup, PopoverTrigger } from "../ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger } from "../ui/select";
import {
  everyMinutesFrom,
  FREQUENCY_OPTIONS,
  intervalAmount,
  intervalUnitOf,
  scheduleLabelOf,
  selectedWeekdays,
  toggleWeekday,
  WEEK_ORDER,
  withFrequency,
  withOptionalField,
  type IntervalUnit,
} from "./automationDraft";

/** The author's own zone, so "09:00" means what they meant when they set it. */
export function browserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "Europe/London";
  } catch {
    return "Europe/London";
  }
}

function PickerField({
  children,
  hint,
  label,
}: {
  readonly children: ReactNode;
  readonly hint?: string | undefined;
  readonly label: string;
}) {
  return (
    <div className="space-y-1.5">
      <span className="text-xs text-muted-foreground">{label}</span>
      {children}
      {hint ? <span className="block text-xs text-muted-foreground/80">{hint}</span> : null}
    </div>
  );
}

/**
 * Seven toggle chips, Monday first. Pressed = runs that day; a weekly rule
 * always keeps one, and an interval with every chip pressed has no day limit.
 */
function DayChips({
  onChange,
  schedule,
}: {
  readonly onChange: (schedule: AutomationSchedule) => void;
  readonly schedule: AutomationSchedule;
}) {
  const selected = selectedWeekdays(schedule);
  return (
    <div role="group" aria-label="Days" className="flex gap-1">
      {WEEK_ORDER.map((day) => {
        const label = WEEKDAY_LABELS[day];
        const pressed = selected.length === 0 || selected.includes(day);
        return (
          <button
            key={day}
            type="button"
            aria-label={label}
            aria-pressed={pressed}
            className={cn(
              "h-7 flex-1 cursor-pointer rounded-md border text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              pressed
                ? "border-primary bg-primary text-primary-foreground"
                : "border-input text-muted-foreground hover:bg-accent hover:text-foreground",
            )}
            onClick={() => onChange(toggleWeekday(schedule, day))}
          >
            {label.slice(0, 2)}
          </button>
        );
      })}
    </div>
  );
}

/** A date input that can be emptied again — "no start" / "no end". */
function OptionalDate({
  label,
  min,
  onChange,
  value,
}: {
  readonly label: string;
  readonly min: string;
  readonly onChange: (value: string) => void;
  readonly value: string | undefined;
}) {
  return (
    <PickerField label={label}>
      <div className="flex items-center gap-1">
        <Input
          type="date"
          className="h-8"
          aria-label={`${label} date`}
          min={min}
          value={value ?? ""}
          onChange={(event) => onChange(event.target.value)}
        />
        {value ? (
          <Button
            variant="ghost"
            size="icon-sm"
            className="shrink-0 text-muted-foreground"
            aria-label={`Clear ${label.toLowerCase()} date`}
            onClick={() => onChange("")}
          >
            <XIcon className="size-3.5" />
          </Button>
        ) : null}
      </div>
    </PickerField>
  );
}

export function SchedulePicker({
  schedule,
  onChange,
}: {
  readonly schedule: AutomationSchedule;
  readonly onChange: (schedule: AutomationSchedule) => void;
}) {
  // The unit is the picker's own: deriving it from the stored minutes would
  // flip "12 minutes" to "2 hours" halfway through typing 120.
  const [unit, setUnit] = useState<IntervalUnit>(() => intervalUnitOf(schedule.everyMinutes));
  const today = zonedDateKey(new Date(), schedule.timezone);
  const frequency = schedule.frequency;
  const amount = intervalAmount(schedule.everyMinutes, unit);
  const timeInput = (
    <Input
      type="time"
      className="h-8"
      aria-label="Time"
      value={schedule.time}
      onChange={(event) => onChange({ ...schedule, time: event.target.value })}
    />
  );

  return (
    <Popover>
      <PopoverTrigger
        render={<Button variant="ghost" size="sm" />}
        className="max-w-60 justify-end gap-1 font-normal text-foreground/90"
      >
        <span className="truncate">{scheduleLabelOf(schedule)}</span>
        <ChevronDownIcon className="size-3.5 shrink-0 text-muted-foreground" />
      </PopoverTrigger>
      <PopoverPopup align="end" className="w-72 space-y-3">
        <PickerField label="Repeats">
          <Select
            value={frequency}
            onValueChange={(value) => {
              const next = withFrequency(schedule, value as AutomationFrequency, new Date());
              if (next.frequency === "interval") setUnit(intervalUnitOf(next.everyMinutes));
              onChange(next);
            }}
          >
            <SelectTrigger size="sm" className="w-full" aria-label="Frequency">
              <span className="truncate">
                {FREQUENCY_OPTIONS.find((option) => option.value === frequency)?.label ?? frequency}
              </span>
            </SelectTrigger>
            <SelectContent>
              {FREQUENCY_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </PickerField>

        {frequency === "once" ? (
          <PickerField label="Date">
            <Input
              type="date"
              className="h-8"
              aria-label="Date"
              min={today}
              value={schedule.date ?? ""}
              onChange={(event) => onChange({ ...schedule, date: event.target.value })}
            />
          </PickerField>
        ) : null}

        {frequency === "interval" ? (
          <>
            <PickerField label="Every">
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  className="h-8 w-20 shrink-0"
                  aria-label="Interval"
                  min={1}
                  step={1}
                  value={Number.isFinite(amount) ? amount : ""}
                  onChange={(event) =>
                    onChange({
                      ...schedule,
                      everyMinutes: everyMinutesFrom(event.target.value, unit),
                    })
                  }
                />
                <Select
                  value={unit}
                  onValueChange={(value) => {
                    const nextUnit = value as IntervalUnit;
                    setUnit(nextUnit);
                    // The number stays; only what it counts changes.
                    onChange({
                      ...schedule,
                      everyMinutes: everyMinutesFrom(
                        Number.isFinite(amount) ? String(amount) : "",
                        nextUnit,
                      ),
                    });
                  }}
                >
                  <SelectTrigger size="sm" className="min-w-0 flex-1" aria-label="Interval unit">
                    <span className="truncate">{unit}</span>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="minutes">minutes</SelectItem>
                    <SelectItem value="hours">hours</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </PickerField>
            <PickerField label="Between" hint="Leave the end empty to run until midnight.">
              <div className="flex items-center gap-2">
                <Input
                  type="time"
                  className="h-8"
                  aria-label="First run each day"
                  value={schedule.time}
                  onChange={(event) => onChange({ ...schedule, time: event.target.value })}
                />
                <span className="text-xs text-muted-foreground">to</span>
                <Input
                  type="time"
                  className="h-8"
                  aria-label="Last run each day"
                  value={schedule.until ?? ""}
                  onChange={(event) =>
                    onChange(withOptionalField(schedule, "until", event.target.value))
                  }
                />
              </div>
            </PickerField>
            <PickerField label="Days">
              <DayChips schedule={schedule} onChange={onChange} />
            </PickerField>
          </>
        ) : null}

        {frequency === "weekly" ? (
          <PickerField label="Days">
            <DayChips schedule={schedule} onChange={onChange} />
          </PickerField>
        ) : null}

        {frequency === "monthly" ? (
          <PickerField
            label="Day of month"
            hint="Days a month lacks run on its last day, so 31 is always the last day."
          >
            <Input
              type="number"
              min={1}
              max={31}
              className="h-8"
              aria-label="Day of month"
              value={schedule.dayOfMonth ?? 1}
              onChange={(event) =>
                onChange({
                  ...schedule,
                  dayOfMonth: Math.min(
                    31,
                    Math.max(1, Math.trunc(Number(event.target.value)) || 1),
                  ),
                })
              }
            />
          </PickerField>
        ) : null}

        {frequency !== "interval" ? <PickerField label="Time">{timeInput}</PickerField> : null}

        {frequency !== "once" ? (
          <div className="grid grid-cols-2 gap-2">
            <OptionalDate
              label="Starts"
              min={today}
              value={schedule.startDate}
              onChange={(value) => onChange(withOptionalField(schedule, "startDate", value))}
            />
            <OptionalDate
              label="Ends"
              min={schedule.startDate && schedule.startDate > today ? schedule.startDate : today}
              value={schedule.endDate}
              onChange={(value) => onChange(withOptionalField(schedule, "endDate", value))}
            />
          </div>
        ) : null}

        <span className="block text-xs text-muted-foreground">
          Times are in {schedule.timezone}
        </span>
      </PopoverPopup>
    </Popover>
  );
}
